<template>
  <div class="covered-tasks">
    <div class="covered-tasks__header">
      <span class="covered-tasks__eyebrow">Covered Tasks</span>
      <span
        v-if="utilization"
        class="covered-tasks__utilization"
        :class="utilizationVariant"
      >
        {{ utilization.completed }} of {{ utilization.total }} completed
      </span>
    </div>
    <div v-if="!groups.length" class="covered-tasks__empty">--</div>
    <div
      v-for="(group, index) in groups"
      :key="group.address_id"
      :class="[
        'covered-tasks__group',
        { 'covered-tasks__group--spaced': index > 0 }
      ]"
    >
      <div class="covered-tasks__address-block">
        <i class="fas fa-map-pin covered-tasks__pin"></i>
        <div class="covered-tasks__address-text">
          <div class="covered-tasks__address">{{ group.address_street }}</div>
          <div class="covered-tasks__last-service">
            <span class="covered-tasks__last-service-label">
              Last service
              <Tooltip placement="top" :width="260">
                <template #trigger>
                  <i
                    class="fas fa-question-circle covered-tasks__help-icon"
                    aria-label="More information"
                  ></i>
                </template>
                <template #tooltip>
                  <span class="covered-tasks__help-tooltip-text">
                    The most recent finalized service at this address —
                    independent of memberships. Click the date to open the job.
                  </span>
                </template>
              </Tooltip>
            </span>
            <router-link
              v-if="group.last_service_date && group.last_service_job_id"
              :to="{
                name: 'JobPage',
                params: { id: group.last_service_job_id }
              }"
              class="covered-tasks__last-service-link"
              @click.stop
              >{{ shortDate(group.last_service_date) }}</router-link
            >
            <span v-else class="covered-tasks__last-service-value">{{
              group.last_service_date
                ? shortDate(group.last_service_date)
                : "--"
            }}</span>
          </div>
        </div>
      </div>
      <div class="covered-tasks__table-scroll">
        <table class="covered-tasks__table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Status</th>
              <th>Cadence</th>
              <th>Available Range</th>
              <th>Completed on</th>
              <th>Invoice</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="task in group.tasks"
              :key="`${group.address_id}-${task.task_name}-${task.slot_number}`"
            >
              <td class="covered-tasks__task-name">{{ task.task_name }}</td>
              <td>
                <Badge
                  :text="task.status"
                  :color="statusColor(task.status)"
                  variant="chip"
                />
              </td>
              <td>{{ task.cadence ?? "--" }}</td>
              <td>
                <template v-if="task.available_from && task.available_until">
                  <div class="covered-tasks__available">
                    <span
                      :class="{
                        'covered-tasks__date--urgent': showDaysChip(task)
                      }"
                      >{{ shortDate(task.available_from) }} -
                      {{ shortDate(task.available_until) }}</span
                    >
                    <Badge
                      v-if="showDaysChip(task)"
                      :text="`${task.days_remaining}d`"
                      :color="daysLeftColor(task.days_remaining!)"
                      variant="chip"
                    />
                  </div>
                </template>
                <template v-else>--</template>
              </td>
              <td>
                <template v-if="task.completed_at">{{
                  shortDate(task.completed_at)
                }}</template>
                <template v-else>--</template>
              </td>
              <td>
                <a
                  v-if="task.invoice_id && task.invoice_url"
                  :href="task.invoice_url"
                  class="covered-tasks__invoice-link"
                  @click.stop
                  >#{{ task.invoice_id }}</a
                >
                <template v-else>--</template>
              </td>
              <td>
                <ScheduleVisitButton
                  v-if="task.status === 'Available' && props.customer"
                  :customer="props.customer"
                  :address="addressFor(group)"
                  :membership_program_item_id="task.membership_program_item_id"
                  :task_name="task.task_name"
                  scheduling_source="covered_tasks"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <AppointmentCreateModal
      v-if="scheduleVisitContext"
      :prefilled_customer="scheduleVisitContext.customer"
      :prefilled_address="scheduleVisitContext.address"
      :membership_program_item_id="
        scheduleVisitContext.membership_program_item_id
      "
      :task_name="scheduleVisitContext.task_name"
      :scheduling_source="scheduleVisitContext.scheduling_source"
      @hide-modal="scheduleVisitContext = null"
      @created="onAppointmentCreated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { EventBus } from "@/lib/event_bus.js";

import Badge from "@/components/Badge.vue";
import Tooltip from "@/components/Tooltip.vue";
import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import ScheduleVisitButton, {
  type ScheduleVisitEventPayload
} from "@/AppointmentCreateModal/ScheduleVisitButton.vue";
import { shortDate } from "@/mixins";
import { Address } from "@/typings/address";
import { Customer } from "@/typings/customer";
import { Job } from "@/typings/job";
import { CoveredTaskGroup, CoveredTaskItem } from "@/typings/memberships";

interface UtilizationSummary {
  total: number;
  completed: number;
}

const props = withDefaults(
  defineProps<{
    groups: CoveredTaskGroup[];
    customer: Customer | null;
    utilization?: UtilizationSummary | null;
  }>(),
  { groups: () => [], customer: null, utilization: null }
);

const utilizationVariant = computed(() => {
  if (!props.utilization) return "";
  if (props.utilization.total === 0) return "";
  if (props.utilization.completed === props.utilization.total)
    return "covered-tasks__utilization--full";
  if (props.utilization.completed === 0)
    return "covered-tasks__utilization--zero";
  return "covered-tasks__utilization--partial";
});

const router = useRouter();
const scheduleVisitContext = ref<ScheduleVisitEventPayload | null>(null);

const addressFor = (group: CoveredTaskGroup): Address =>
  ({
    id: group.address_id,
    street: group.address_street,
    unit: group.address_unit ?? "",
    city: group.address_city ?? "",
    state: group.address_state ?? "",
    zip_code: group.address_zip_code ?? ""
  } as Address);

const handleOpenScheduleVisitModal = (payload: ScheduleVisitEventPayload) => {
  scheduleVisitContext.value = payload;
};

const onAppointmentCreated = (job: Job) => {
  scheduleVisitContext.value = null;
  router.push(`/jobs/${job.id}`);
};

onMounted(() => {
  EventBus.$on("open-schedule-visit-modal", handleOpenScheduleVisitModal);
});

onUnmounted(() => {
  EventBus.$off("open-schedule-visit-modal", handleOpenScheduleVisitModal);
});

const DAYS_NEARING = 60;
const DAYS_WARNING = 30;
const DAYS_DANGER = 14;

function statusColor(status: CoveredTaskItem["status"]) {
  switch (status) {
    case "Completed":
      return "green";
    case "Scheduled":
      return "blue";
    case "Lapsed":
    case "Upcoming":
      return "gray";
    default:
      return "yellow";
  }
}

function showDaysChip(task: CoveredTaskItem): boolean {
  return (
    task.status !== "Completed" &&
    task.status !== "Scheduled" &&
    task.status !== "Lapsed" &&
    task.days_remaining !== null &&
    task.days_remaining < DAYS_NEARING
  );
}

function daysLeftColor(days: number): "red" | "yellow" | "blue" {
  if (days < DAYS_DANGER) return "red";
  if (days < DAYS_WARNING) return "yellow";
  return "blue";
}
</script>

<style lang="scss" scoped>
.covered-tasks {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--gray-border-color);
}
.covered-tasks__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.875rem;
}
.covered-tasks__eyebrow {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #64748b;
}

.covered-tasks__utilization {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 0.0625rem 0.5rem;
  border-radius: 9999px;
  background: #eff6ff;
  color: #1d4ed8;
}
.covered-tasks__utilization--full {
  background: #ecfdf5;
  color: #047857;
}
.covered-tasks__utilization--zero {
  background: #fffbeb;
  color: #b45309;
}
.covered-tasks__utilization--partial {
  background: #eff6ff;
  color: #1d4ed8;
}
.covered-tasks__empty {
  color: var(--gray);
}

.covered-tasks__group--spaced {
  margin-top: 1.5rem;
}

// Pin in a fixed-width gutter so the address text + Last service share a
// left edge automatically — hand-tuned margin offsets drift with icon width.
.covered-tasks__address-block {
  display: grid;
  grid-template-columns: 1.25rem 1fr;
  column-gap: 0.5rem;
  align-items: start;
  margin-bottom: 0.75rem;
}
.covered-tasks__pin {
  color: #94a3b8;
  font-size: 0.875rem;
  line-height: 1.4;
  padding-top: 0.125rem;
  text-align: center;
}
.covered-tasks__address-text {
  min-width: 0;
}
.covered-tasks__address {
  font-weight: 600;
  font-size: 0.9375rem;
  color: #0f172a;
  line-height: 1.3;
}
.covered-tasks__last-service {
  margin-top: 0.1875rem;
  font-size: 0.75rem;
  color: var(--gray);
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}
.covered-tasks__last-service-label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  font-size: 0.6875rem;
  cursor: help;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
.covered-tasks__help-icon {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-left: 0.125rem;
  cursor: help;

  &:hover {
    color: #64748b;
  }
}
.covered-tasks__help-tooltip-text {
  display: block;
  line-height: 1.4;
  font-size: 0.8125rem;
}
.covered-tasks__last-service-value {
  color: #0f172a;
  font-weight: 500;
  font-size: 0.8125rem;
}
.covered-tasks__last-service-link {
  color: var(--blue);
  font-weight: 500;
  font-size: 0.8125rem;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// table min-width + scroll wrapper: columns stay readable; the gutter is
// pulled into the scroll surface so mobile can swipe edge-to-edge.
.covered-tasks__table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    margin: 0 -1.25rem;
    padding: 0 1.25rem;
  }
}

.covered-tasks__table {
  width: 100%;
  min-width: 36rem;
  border-collapse: collapse;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }
  thead th {
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
    border-bottom: 1px solid var(--gray-border-color);
    padding-top: 0.375rem;
    padding-bottom: 0.5rem;
  }
  tbody tr {
    border-bottom: 1px solid #f1f5f9;
    &:last-of-type {
      border-bottom: none;
    }
  }
  tbody td {
    font-size: 0.875rem;
    color: #1f2937;
    vertical-align: middle;
  }
}
.covered-tasks__task-name {
  font-weight: 500;
}
.covered-tasks__available {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.covered-tasks__date--urgent {
  font-weight: 600;
}
.covered-tasks__invoice-link {
  font-weight: 500;
}
</style>
